import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, PauseIcon } from 'lucide-react';
import { GlowButton } from './GlowButton';
import { useCurrencyPrice } from '../hooks/useCurrencyPrice';
import { useLanguage } from '../contexts/LanguageContext';
const AUDIO_PREVIEW_URL = 'https://github.com/jvzng/webpage_chatbot_closer/raw/980fc46fbc1f0174fb4817a62896dc782237fcd9/ElevenLabs_2026-02-19T20_15_33_Declan%20Sage%20-%20Wise%2C%20Deliberate%2C%20Captivating_pvc_sp76_s50_sb50_se48_b_m2.mp3';
export function HeroSection() {
  const {
    price,
    originalPrice,
    loading
  } = useCurrencyPrice();
  const {
    t,
    coverImage,
    audioUrl,
    checkoutUrl
  } = useLanguage();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const cur = audioRef.current.currentTime;
    const dur = audioRef.current.duration;
    setCurrentTime(cur);
    if (dur) setProgress(cur / dur * 100);
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audioRef.current.currentTime = (e.clientX - rect.left) / rect.width * duration;
  };
  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };
  const fmt = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
  return <section className="relative w-full flex items-center justify-center overflow-hidden pt-4 pb-8 sm:min-h-screen sm:py-0" data-id="element-126">
      {/* Animated psychedelic background gradients */}
      <div className="absolute inset-0 gpu-layer" data-id="element-127">
        <div className="absolute top-0 left-1/4 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-flame/10 rounded-full blur-[80px] sm:blur-[150px] animate-pulse" data-id="element-128" />
        <div className="absolute bottom-0 right-1/4 w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] bg-magenta/10 rounded-full blur-[60px] sm:blur-[120px] animate-pulse" style={{
        animationDelay: '1.5s'
      }} data-id="element-129" />
        <div className="hidden sm:block absolute top-1/3 right-1/3 w-[400px] h-[400px] bg-teal/10 rounded-full blur-[100px] animate-pulse" style={{
        animationDelay: '3s'
      }} data-id="element-130" />
        <div className="hidden sm:block absolute bottom-1/3 left-1/3 w-[350px] h-[350px] bg-purple-electric/8 rounded-full blur-[120px] animate-pulse" style={{
        animationDelay: '2s'
      }} data-id="element-131" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-0 pb-6 sm:py-20 flex flex-col lg:flex-row items-center gap-6 lg:gap-14" data-id="element-132">
        {/* Text content */}
        <div className="flex-1 text-center lg:text-left" data-id="element-133">
          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          ease: [0.22, 1, 0.36, 1]
        }} className="font-serif sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-8 text-[28px]" data-id="element-134">
            {t('hero.title.part1')}
            <br data-id="element-135" />
            <span className="bg-clip-text bg-gradient-to-r from-flame via-gold-light to-flame-light text-[#E8C97A] font-[Cinzel,_serif] text-[28px]" data-id="element-136">
              {t('hero.title.part2')}
            </span>
          </motion.h1>

          {/* Audio player + price + CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-6 sm:mb-10 max-w-xl mx-auto lg:mx-0 flex flex-col items-center lg:items-start gap-4" data-id="element-137">
            {/* Audio player */}
            <div className="w-full rounded-xl bg-white/[0.04] border border-purple-electric/15 p-4" data-id="element-138">
              <audio ref={audioRef} src={audioUrl} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} preload="metadata" data-id="element-139" />
              <div className="mb-3" data-id="element-140">
                <p className="font-sans text-xs text-white/80 font-medium leading-snug mb-0.5" data-id="element-141">
                  {t('hero.audioWarning')}
                </p>
                <p className="font-sans text-[11px] leading-snug text-[#FFFFFF]" data-id="element-142">
                  {t('hero.audioDescription')}
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-143">
                <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-purple-electric/20 border border-purple-electric/30 flex items-center justify-center flex-shrink-0 hover:bg-purple-electric/30 transition-colors duration-300" data-id="element-144">
                  {isPlaying ? <PauseIcon className="w-4 h-4 text-purple-electric-light" data-id="element-145" /> : <PlayIcon className="w-4 h-4 text-purple-electric-light ml-0.5" data-id="element-146" />}
                </button>
                <div className="flex-1 min-w-0" data-id="element-147">
                  <div className="w-full h-1.5 bg-white/[0.06] rounded-full cursor-pointer group" onClick={handleSeek} data-id="element-148">
                    <div className="h-full bg-gradient-to-r from-purple-electric to-purple-electric-light rounded-full transition-all duration-100 relative" style={{
                    width: `${progress}%`
                  }} data-id="element-149">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-purple-electric-light rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-150" />
                    </div>
                  </div>
                  <div className="flex justify-between mt-1.5" data-id="element-151">
                    <span className="font-sans text-[10px] text-white/25" data-id="element-152">
                      {fmt(currentTime)}
                    </span>
                    <span className="font-sans text-[10px] text-white/25" data-id="element-153">
                      {duration ? fmt(duration) : '--:--'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Price display */}
            <div className="flex flex-col items-center lg:items-start gap-1.5" data-id="element-154">
              <div className="flex items-center gap-3" data-id="element-155">
                <span className="font-sans text-white/30 text-sm line-through" data-id="element-156">
                  {t('hero.priceBefore')} {loading ? '$60 USD' : originalPrice}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-flame/15 border border-flame/25 text-flame-light text-xs font-sans font-medium tracking-wide" data-id="element-157">
                  {t('hero.limitedTime')}
                </span>
              </div>
              <span className="font-serif text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold-light to-gold" data-id="element-158">
                {loading ? '$14.99 USD' : price}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/15 border border-teal/30 text-white/70 font-sans font-medium tracking-wide text-[18px]" data-id="element-159">
                {t('hero.bonusBadge')}
              </span>
            </div>

            <GlowButton href="#cta-pago" mobileHref={checkoutUrl} variant="gold" data-id="element-160">
              {t('hero.cta')}
            </GlowButton>
            <p className="font-sans text-[11px] text-white/30 tracking-wide text-center lg:text-left" data-id="element-161">
              {t('hero.trustLine')}
            </p>
          </motion.div>

          {/* Format badges */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-center gap-3 justify-center lg:justify-start" data-id="element-162">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] backdrop-blur-sm border border-white/[0.08] text-white/70 text-sm font-sans" data-id="element-163">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-flame-light" data-id="element-164">
                <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" data-id="element-165" />
              </svg>
              {t('hero.ebook')}
            </span>
            <span className="text-white/20 text-xs" data-id="element-166">+</span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] backdrop-blur-sm border border-white/[0.08] text-white/70 text-sm font-sans" data-id="element-167">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-electric-light" data-id="element-168">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" data-id="element-169" />
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" data-id="element-170" />
              </svg>
              {t('hero.audiobook')}
            </span>
          </motion.div>
        </div>

        {/* Book cover image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.85
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.2,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex-shrink-0" data-id="element-171">
          <div className="relative animate-float gpu-layer" data-id="element-172">
            <div className="absolute -inset-8 sm:-inset-12 bg-gradient-to-br from-flame/30 via-magenta/20 to-teal/25 rounded-3xl blur-2xl sm:blur-3xl animate-pulse" data-id="element-173" />
            <div className="hidden sm:block absolute -inset-8 bg-gradient-to-tr from-purple-electric/20 via-flame/15 to-gold/20 rounded-2xl blur-2xl" data-id="element-174" />
            <div className="hidden sm:block absolute -inset-4 bg-gradient-to-bl from-teal/15 via-magenta/10 to-flame/15 rounded-xl blur-xl" data-id="element-175" />

            <div className="relative w-56 sm:w-72 rounded-lg overflow-hidden shadow-glow-psyche animate-psyche-pulse" data-id="element-176">
              <img src={coverImage} alt={t('bookCover.alt')} loading="eager" decoding="async" fetchPriority="high" className="w-full h-auto object-cover rounded-lg" data-id="element-177" />
              <div className="absolute inset-0 bg-gradient-to-t from-void/20 via-transparent to-transparent pointer-events-none" data-id="element-178" />
            </div>

            <div className="absolute -top-4 -right-4 w-3 h-3 bg-flame rounded-full blur-sm animate-pulse" data-id="element-179" />
            <div className="absolute -bottom-5 -left-3 w-2.5 h-2.5 bg-magenta rounded-full blur-sm animate-pulse" style={{
            animationDelay: '0.5s'
          }} data-id="element-180" />
            <div className="hidden sm:block absolute top-1/4 -right-6 w-2 h-2 bg-teal-light rounded-full blur-sm animate-pulse" style={{
            animationDelay: '1s'
          }} data-id="element-181" />
            <div className="hidden sm:block absolute bottom-1/4 -left-5 w-2 h-2 bg-purple-electric-light rounded-full blur-sm animate-pulse" style={{
            animationDelay: '1.5s'
          }} data-id="element-182" />
            <div className="hidden sm:block absolute top-1/2 -right-8 w-1.5 h-1.5 bg-gold-light rounded-full blur-sm animate-pulse" style={{
            animationDelay: '2s'
          }} data-id="element-183" />
            <div className="hidden sm:block absolute -top-2 left-1/4 w-1.5 h-1.5 bg-flame-bright rounded-full blur-sm animate-pulse" style={{
            animationDelay: '2.5s'
          }} data-id="element-184" />
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2,
      duration: 1
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block" data-id="element-185">
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="w-5 h-8 rounded-full border border-flame/40 flex items-start justify-center p-1.5" data-id="element-186">
          <div className="w-1 h-2 bg-flame/60 rounded-full" data-id="element-187" />
        </motion.div>
      </motion.div>
    </section>;
}